<template>
  <div @click.stop>
    <m-button-select-tooltip
      v-if="hasAssignmentPermission"
      :options="assigneeOptions"
      :value="selectedValue"
      :shadow-value="shouldUseShadowValue ? selectedValue : null"
      :shadow-label="shouldUseShadowValue ? displayValue : null"
      :readonly="isDisabled"
      @input="handleChange" />
    <span v-else class="text-item">{{ displayValue }}</span>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent, PropType} from '@vue/composition-api'

import MButtonSelectTooltip from '@/components/leads/MButtonSelectTooltip.vue'
import {AgentAssignment, LeadStatus} from '@/types'

export default defineComponent({
  name: 'LeadAssigneeSelect',
  components: {MButtonSelectTooltip},
  props: {
    agent: {
      type: Object as PropType<AgentAssignment>,
      default: () => ({}),
    },
    initialAgent: {
      type: Object as PropType<AgentAssignment>,
      default: () => ({}),
    },
    dealerUsers: {
      type: Array as PropType<Array<{label: string; value: string}>>,
      default: () => [],
    },
    isDealerAdmin: {
      type: Boolean,
      default: false,
    },
    isSalesAgent: {
      type: Boolean,
      default: false,
    },
    userEmail: {
      type: String,
      default: '',
    },
    status: {
      type: String,
      default: '',
    },
  },
  setup(props, {root, emit}) {
    const isLegacyAssignedLead = computed(() => {
      return !!props.agent?.agent && !props.agent?.agentEmail
    })

    // Use shadow value/label when:
    // 1. It's a legacy assigned lead (agent name not in dealerUsers)
    // 2. It's a regular assigned lead but the email isn't in dealerUsers yet (still loading)
    // 3. Value is in options but the label doesn't match (e.g., sales-agent viewing own lead shows "Asignarme" instead of name)
    const shouldUseShadowValue = computed(() => {
      // CASE 1: Legacy leads always use shadow to display legacy agent name
      // (legacy agent not in dealerUsers, so we need shadow to show the name)
      if (isLegacyAssignedLead.value) return true

      // CASE 2 & 3: For regular assigned leads, check if shadow is needed
      if (isAssigned.value && selectedValue.value) {
        const matchingOption = assigneeOptions.value.find(opt => opt.value === selectedValue.value)

        // CASE 2: Value not in options - use shadow to display the value
        // (e.g., dealerUsers still loading or agent removed from list)
        if (!matchingOption) return true

        // CASE 3: Value in options but label mismatch - use shadow to show correct label
        // Common scenario: sales-agent viewing own lead - dropdown shows "Asignarme" but
        // we want to display their actual name, so we use shadow to override the label
        if (matchingOption.label !== displayValue.value) return true

        // Exact match found - no shadow needed
        return false
      }

      // Unassigned lead - no shadow needed
      return false
    })

    const isAssigned = computed(() => {
      return !!(props.agent?.agentEmail || props.agent?.agent)
    })

    const hasAssignmentPermission = computed(() => {
      return props.isDealerAdmin || props.isSalesAgent
    })

    const hasDealerUsers = computed(() => {
      return Array.isArray(props.dealerUsers) && props.dealerUsers.length > 0
    })

    const isStatusRestricted = computed(() => {
      return props.status === LeadStatus.WithOffer || props.status === LeadStatus.Lost
    })

    // Disable dropdown for:
    // - Legacy leads (sales-agents only - dealer-admins CAN reassign)
    // - Restricted statuses (With Offer / Lost) - ALL roles
    // - Sales-agent if lead is already assigned
    // - Users without assignment permission (not dealer-admin or sales-agent)
    const isDisabled = computed(() => {
      if (!hasAssignmentPermission.value) return true
      if (isStatusRestricted.value) return true

      // Dealer admin has highest priority
      if (props.isDealerAdmin) return false

      if (props.isSalesAgent) {
        // Sales agents cannot reassign legacy leads or already assigned leads
        if (isLegacyAssignedLead.value) return true
        return isAssigned.value
      }

      return true
    })

    const displayValue = computed(() => {
      if (isLegacyAssignedLead.value) {
        return props.agent?.agent || root.$t('leads.list.unassigned').toString()
      }

      // Always show agent name first if available, otherwise email
      return props.agent?.agent || props.agent?.agentEmail || root.$t('leads.list.unassigned').toString()
    })

    const selectedValue = computed(() => {
      // For legacy leads, show the legacy agent name even though it's not in options
      // m-button-select supports displaying a value not in the options array
      if (isLegacyAssignedLead.value) {
        return props.agent?.agent || ''
      }
      return props.agent?.agentEmail || ''
    })

    const assigneeOptions = computed(() => {
      // LEGACY LEADS FOR SALES-AGENT: Show only the legacy agent (read-only via disabled state)
      if (isLegacyAssignedLead.value && props.isSalesAgent && !props.isDealerAdmin && props.agent?.agent) {
        return [{label: props.agent.agent, value: props.agent.agent}]
      }

      // LEGACY LEADS FOR DEALER-ADMIN: Show only dealer users in dropdown
      // Selected value can still display legacy agent even if not in options
      if (isLegacyAssignedLead.value && props.isDealerAdmin) {
        return hasDealerUsers.value ? props.dealerUsers : []
      }

      // For sales-agent: show only unassigned and self-assign options
      if (props.isSalesAgent && !props.isDealerAdmin) {
        const options: Array<{label: string; value: string}> = []

        // Always show "Unassigned" and "Assign to me" for unassigned leads
        // or just read-only view handled by disabled state if assigned
        options.push({label: root.$t('leads.list.unassigned').toString(), value: ''})
        options.push({
          label: root.$t('leads.list.assign-to-me').toString(),
          value: props.userEmail,
        })

        // If lead is assigned to someone else, show their name (read-only via disabled dropdown)
        if (isAssigned.value && props.agent?.agentEmail && props.agent.agentEmail !== props.userEmail) {
          const agentLabel = props.agent?.agent || props.agent?.agentEmail
          // Find if agent exists in the base options to get their proper name
          const existingAgent = props.dealerUsers.find(u => u.value === props.agent?.agentEmail)
          options.push({
            label: existingAgent?.label || agentLabel,
            value: props.agent.agentEmail,
          })
        }

        return options
      }

      // For dealer-admin
      const options: Array<{label: string; value: string}> = []

      // Only show "Unassigned" option if lead is not already assigned
      if (!isAssigned.value) {
        options.push({label: root.$t('leads.list.unassigned').toString(), value: ''})
      }

      if (hasDealerUsers.value) {
        options.push(...props.dealerUsers)
      }

      return options
    })

    function handleChange(newValue: string) {
      emit('input', newValue)
    }

    return {
      assigneeOptions,
      selectedValue,
      isLegacyAssignedLead,
      shouldUseShadowValue,
      isDisabled,
      hasAssignmentPermission,
      hasDealerUsers,
      displayValue,
      handleChange,
    }
  },
})
</script>
